import { Flex, Stack, Text, useBreakpointValue, useTheme } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import ButtonNavigation from 'components/button-navigation'
import Heading from 'components/heading'
import PasswordReset from 'components/password-reset'
import { useUserContext } from 'contexts/user-context'
import { completeNewPassword } from 'utils/auth-utils'

import type { PasswordResetCallback } from 'components/password-reset'

export default function ResetPassword() {
  const theme = useTheme()
  const { state } = useLocation()
  const { signOut } = useUserContext()
  const navigate = useNavigate()
  const isSmallViewport = useBreakpointValue({ base: true, md: false })
  const [activeScreen, setActiveScreen] = useState(0)
  const [user] = useState(state?.user ?? null)
  const [username] = useState(state?.username ?? '')
  const [loading, setLoading] = useState(false)

  const createCognitoUserClass = () => {
    const pool = new CognitoUserPool({
      UserPoolId: user.pool.userPoolId,
      ClientId: user.pool.clientId,
      endpoint: user.client.endpoint,
      Storage: window.localStorage,
      AdvancedSecurityDataCollectionFlag: user.advancedSecurityDataCollectionFlag,
    })
    const cognitoUser = new CognitoUser({
      Username: user.username,
      Pool: pool,
      Storage: window.localStorage,
    })
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    cognitoUser.Session = user.Session
    return cognitoUser
  }

  const sendCompleteNewPassword = async ({ newPassword }: PasswordResetCallback) => {
    await completeNewPassword({ user: createCognitoUserClass(), newPassword })
    signOut() // NB: completing new password returns a session, but we need the user to login properly
    setLoading(false)
    setActiveScreen((activeScreen ?? 0) + 1)
  }

  useEffect(() => {
    // prevent direct access to this route if missing the "user" state
    if (!state?.user) navigate('/')
  }, [navigate, state?.user])

  return (
    <Flex w="100%" h="100vh" align="center" direction="column" background={theme.baseColors.background.default}>
      <Stack
        w={isSmallViewport ? '100%' : '50%'}
        direction="column"
        spacing="32px"
        my={12}
        px={{ base: 6, xl: 24 }}
        maxW="1280px"
        borderTopRadius="base"
      >
        {activeScreen === 0 && (
          <PasswordReset loading={loading} setLoading={setLoading} callback={sendCompleteNewPassword} />
        )}
        {activeScreen === 1 && (
          <>
            <FontAwesomeIcon icon={['fas', 'circle-check']} size="8x" color={theme.baseColors.secondary.dark} />
            <Stack direction="column" spacing="12px">
              <Heading as="h2" text="Success!" />
              <Text>Your password has been changed.</Text>
            </Stack>
            <ButtonNavigation
              w="100%"
              type="progress"
              text="Sign In"
              onClick={() => navigate('/login', { state: { username } })}
            />
          </>
        )}
      </Stack>
    </Flex>
  )
}
